"use client";

import type { SendTurnRequest, SubmitWidgetRequest } from "@health/sdk-client";
import type { Brand } from "@health/types";
import { getToolNameFromPart, isWidgetToolPart, resolveToolName } from "@health/types/ui-tools";
import {
  ChatErrorCard,
  ChatFeed,
  type ChatItem,
  ChatLayout,
  type ChatStatusItem,
  type ChatWidgetItem,
  Spinner,
  StatusIndicator,
  useStylesConfig,
  useStylesConfigValue,
} from "@health/ui";
import type { RecapData } from "@health/ui/components/recap-card";

import { cn } from "@health/ui/utils";
import type { ChatStatus, UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useChatClient } from "./ChatProvider";
import { hasVisibleStatus } from "./chat/chat-tool-config";
import { defaultRenderText } from "./chat/default-render-text";
import {
  getVisibleMessageFingerprint,
  isUserMessagePart,
  normalizeChatItems,
} from "./chat/normalize";
import { AnalyticsProvider } from "./internal/analytics/context";
import {
  resetLatestServerTurnIndex,
  setLatestServerTurnIndex,
} from "./internal/analytics/turn-index-tracker";
import {
  markTurnStart,
  resetTurnTimingRegistry,
  TurnTimingProvider,
} from "./internal/analytics/useTurnTiming";
import { ActiveFormLayer } from "./internal/chat/ActiveFormLayer";
import { ChatInput } from "./internal/chat/ChatInput";
import {
  ACTIVITY_STATUS_AUTO_FADE_MS,
  ToolActivityStatus,
} from "./internal/chat/ToolActivityStatus";
import { THINKING_COPY_POOL, THINKING_COPY_ROTATE_MS } from "./internal/chat/tool-status-copy";
import { isMeducationSuppressed } from "./internal/widgets/MeducationWidget";
import { WidgetLoadingRegistry } from "./internal/widgets/WidgetLoadingRegistry";
import { preloadWidgetChunk, WidgetRegistry } from "./internal/widgets/WidgetRegistry";

type DebugPanelRenderProps = {
  sessionId: string;
  chatStatus: ChatStatus;
  siteId: Brand;
  onSiteIdChange: (id: string) => void;
  startingCategory: string;
  onStartingCategoryChange: (id: string) => void;
  onClearHistory: () => void;
  onClearConversation: () => void;
  isOpen: boolean;
  onToggle: () => void;
};

type ChatPanelProps = {
  enableLoopDetection?: boolean;
  renderDebugPanel?: (props: DebugPanelRenderProps) => React.ReactNode;
  showRecap?: boolean;
  /** Override initial site ID (brand). Seeds internal state, falls back to localStorage. */
  initialSiteId?: Brand;
  /** Override initial starting category. Seeds internal state, falls back to ?cap= or "weight-loss". */
  initialStartingCategory?: string;
  /** Surface intent for page context. "browsing" = landing page, "focused" = category detail. */
  surfaceIntent?: "browsing" | "focused" | "comparing";
  /** Called when a tool call is detected in the assistant stream. SDK-owned event type. */
  onToolCall?: (event: { toolName: string; args: Record<string, unknown> }) => void;
  /** Called when the message list changes. Used by JourneyProvider to observe progress. */
  onMessagesChange?: (messages: UIMessage[]) => void;
};

type ChatState = {
  messages: UIMessage[];
  status: ChatStatus;
  error: string | null;
  historyLoaded: boolean;
  hasMoreHistory: boolean;
  oldestCursor: string | null;
  loadingMore: boolean;
  historySummary: string | null;
  greetingLoading: boolean;
  greetingText: string | null;
  greetingChips: string[];
  hasStoredData: boolean;
  recapData: RecapData | null;
  historyExpanded: boolean;
  loadingHistory: boolean;
  hasActiveForm: boolean;
  formFieldContext: { placeholder?: string; fieldType?: string } | null;
};

type ChatAction =
  | { type: "reset"; messages: UIMessage[] }
  | { type: "set_messages"; messages: UIMessage[] }
  | { type: "stream_start"; messages: UIMessage[] }
  | { type: "stream_update"; message: UIMessage }
  | { type: "stream_error"; error: string }
  | { type: "stream_finish" }
  | {
      type: "history_loaded";
      messages: UIMessage[];
      hasStoredData: boolean;
      recapData: RecapData | null;
      greetingLoading: boolean;
    }
  | {
      type: "set_greeting";
      recapData: RecapData | null;
      greeting: string | null;
      chips: string[];
      loading: boolean;
    }
  | {
      type: "prepend_history";
      messages: UIMessage[];
      hasMore: boolean;
      oldestCursor: string | null;
      summary: string | null;
    }
  | {
      type: "history_page_state";
      loadingMore?: boolean;
      loadingHistory?: boolean;
      hasMoreHistory?: boolean;
      oldestCursor?: string | null;
      historySummary?: string | null;
      historyExpanded?: boolean;
    }
  | {
      type: "set_active_form";
      active: boolean;
      fieldContext: { placeholder?: string; fieldType?: string } | null;
    }
  | { type: "set_stored_data"; hasStoredData: boolean; recapData: RecapData | null };

const SESSION_HISTORY_PAGE_SIZE = 30;
const EXPERIENCE_VARIANT_COOKIE = "EXPERIENCE_VARIANT";

function readExperienceVariantOverride(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const urlVariant = new URLSearchParams(window.location.search).get("variant");
  if (urlVariant) return urlVariant;

  const cookieMatch = document.cookie.match(new RegExp(`${EXPERIENCE_VARIANT_COOKIE}=([^;]+)`));
  return cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]) : undefined;
}

const WELCOME_MESSAGES: Record<string, string> = {
  explorer:
    "I can help you explore our health and wellness options. From weight management to skincare, mental health to lab testing — there's a lot we can help with.\n\nWhat brings you here today?",
  "weight-loss":
    "Whether you're curious about GLP-1 medications, lifestyle programs, or just want to learn more — I'm happy to chat.\n\nWhat brings you here today?",
  hair: "Whether you're noticing thinning, a receding hairline, or just want to be proactive — I can help you explore your options.\n\nWhat brings you here today?",
  skin: "From acne to anti-aging, I can help you find the right prescription skincare routine.\n\nWhat brings you here today?",
  ed: "I can help you explore treatment options for erectile dysfunction — discreetly and on your terms.\n\nWhat brings you here today?",
  menopause:
    "Whether it's hot flashes, mood changes, or sleep issues — I can help you explore treatment options for menopause.\n\nWhat brings you here today?",
  "mental-health":
    "I can help you explore options for managing anxiety or depression, including medication and support.\n\nWhat brings you here today?",
  "sexual-health":
    "I can help you explore options for sexual wellness and intimacy.\n\nWhat brings you here today?",
  "birth-control":
    "I can help you find the right birth control option delivered to your door.\n\nWhat brings you here today?",
  labs: "I can help you understand which lab tests might be right for you — from hormone panels to metabolic health.\n\nWhat brings you here today?",
};

const HERO_TEXT: Record<string, { heading: string; subheading: string }> = {
  explorer: {
    heading: "Hi there! What can we help you with?",
    subheading: "Explore treatments for weight loss, hair, skin, mental health, and more.",
  },
  "weight-loss": {
    heading: "Hi there! Let's start your weight loss journey",
    subheading: "Learn about treatment options based on your goals, habits, and health history.",
  },
  hair: {
    heading: "Hi there! Let's talk about hair health",
    subheading: "Explore prescription treatments for hair thinning and loss.",
  },
  skin: {
    heading: "Hi there! Let's find your skincare routine",
    subheading: "Explore prescription skincare tailored to your skin concerns.",
  },
  ed: {
    heading: "Hi there! Let's explore ED treatments",
    subheading: "Discreet, effective options for erectile dysfunction.",
  },
  menopause: {
    heading: "Hi there! Let's talk about menopause care",
    subheading: "Explore treatments for hot flashes, mood changes, and more.",
  },
  "mental-health": {
    heading: "Hi there! Let's talk about mental health",
    subheading: "Explore medication and support options for anxiety and depression.",
  },
  "sexual-health": {
    heading: "You're on your way to better sex.",
    subheading: "Let's go through a few questions and tailor this to your goals.",
  },
  "birth-control": {
    heading: "Hi there! Let's find your birth control",
    subheading: "Prescription birth control delivered right to your door.",
  },
  labs: {
    heading: "Hi there! Let's explore lab testing",
    subheading: "At-home lab tests for hormones, metabolic health, and more.",
  },
};

const FALLBACK_URLS: Record<string, Record<Brand, string>> = {
  "weight-loss": {
    hers: "https://www.forhers.com/g/i/wm/your-goal",
    hims: "https://www.forhims.com/g/i/wm/your-goal",
  },
  hair: { hers: "https://www.forhers.com/hair", hims: "https://www.forhims.com/hair" },
  skin: { hers: "https://www.forhers.com/skin", hims: "https://www.forhims.com/skin" },
  labs: { hers: "https://www.forhers.com/labs", hims: "https://www.forhims.com/labs" },
};

function getWelcomeText(capability: string): string {
  return WELCOME_MESSAGES[capability] || WELCOME_MESSAGES.explorer;
}

function createWelcomeMessage(category: string, welcomeText?: string): UIMessage {
  return {
    id: "welcome",
    role: "assistant",
    parts: [{ type: "text", text: welcomeText ?? getWelcomeText(category) }],
  };
}

function getHeroText(capability: string) {
  return HERO_TEXT[capability] || HERO_TEXT.explorer;
}

function getFallbackUrl(capability: string, brand: Brand): string {
  const capabilityUrls = FALLBACK_URLS[capability];
  if (capabilityUrls) return capabilityUrls[brand];
  return brand === "hims" ? "https://www.forhims.com" : "https://www.forhers.com";
}

const SYNTHETIC_CHIP_IDS = new Set(["starter-chips", "greeting-chips"]);

function syncToUrl(params: Record<string, string | null>) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  for (const [key, value] of Object.entries(params)) {
    if (value === null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  }
  window.history.replaceState(null, "", url.toString());
}

function getTranscriptStorageKey(sessionId: string) {
  return `companion-transcript:${sessionId}`;
}

function readCachedTranscript(sessionId: string): UIMessage[] {
  if (typeof window === "undefined") return [];
  const raw = window.sessionStorage.getItem(getTranscriptStorageKey(sessionId));
  if (!raw) return [];

  try {
    return JSON.parse(raw) as UIMessage[];
  } catch {
    window.sessionStorage.removeItem(getTranscriptStorageKey(sessionId));
    return [];
  }
}

function persistCachedTranscript(sessionId: string, messages: UIMessage[]) {
  if (typeof window === "undefined") return;
  if (messages.length === 0) {
    window.sessionStorage.removeItem(getTranscriptStorageKey(sessionId));
    return;
  }

  window.sessionStorage.setItem(getTranscriptStorageKey(sessionId), JSON.stringify(messages));
}

function clearCachedTranscript(sessionId: string) {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(getTranscriptStorageKey(sessionId));
}

function replaceOrAppendAssistantMessage(messages: UIMessage[], message: UIMessage) {
  const nextMessages = [...messages];
  const lastIndex = nextMessages.length - 1;
  if (lastIndex >= 0 && nextMessages[lastIndex]?.role === "assistant") {
    nextMessages[lastIndex] = message;
    return nextMessages;
  }
  nextMessages.push(message);
  return nextMessages;
}

function updateToolOutput(
  messages: UIMessage[],
  options: { toolCallId: string; output: unknown },
): UIMessage[] {
  return messages.map((message) => ({
    ...message,
    parts: message.parts.map((part) => {
      if (!part.type.startsWith("tool-")) return part;
      const toolPart = part as {
        type: string;
        toolCallId: string;
        state?: string;
        input?: unknown;
        output?: unknown;
      };
      if (toolPart.toolCallId !== options.toolCallId) return part;
      return {
        ...toolPart,
        type: toolPart.type as `tool-${string}`,
        state: "output-available" as const,
        output: options.output,
      };
    }),
  })) as UIMessage[];
}

function getLastAssistantMessage(messages: UIMessage[]) {
  return [...messages].reverse().find((message) => message.role === "assistant");
}

function getWidgetToolKey(message: UIMessage | undefined) {
  if (!message) return null;
  const toolIds = message.parts
    .filter((part) => isWidgetToolPart(part))
    .map((part) => (part as { toolCallId: string }).toolCallId)
    .sort();
  return toolIds.length > 0 ? toolIds.join(",") : null;
}

function allWidgetOutputsAvailable(message: UIMessage | undefined) {
  if (!message) return false;
  const widgetParts = message.parts.filter((part) => isWidgetToolPart(part));
  if (widgetParts.length === 0) return false;
  return widgetParts.every((part) => {
    const toolPart = part as { state?: string };
    return toolPart.state === "output-available" || toolPart.state === "output-error";
  });
}

function reducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "reset":
      return {
        ...state,
        messages: action.messages,
        status: "ready",
        error: null,
        historyLoaded: true,
        hasMoreHistory: false,
        oldestCursor: null,
        loadingMore: false,
        historySummary: null,
        greetingLoading: false,
        greetingText: null,
        greetingChips: [],
        hasStoredData: false,
        recapData: null,
        historyExpanded: false,
        loadingHistory: false,
      };
    case "set_messages":
      return { ...state, messages: action.messages };
    case "stream_start":
      return { ...state, messages: action.messages, status: "submitted", error: null };
    case "stream_update":
      return {
        ...state,
        status: "streaming",
        messages: replaceOrAppendAssistantMessage(state.messages, action.message),
      };
    case "stream_finish":
      return { ...state, status: "ready" };
    case "stream_error":
      return { ...state, status: "error", error: action.error };
    case "history_loaded":
      return {
        ...state,
        messages: action.messages,
        historyLoaded: true,
        hasStoredData: action.hasStoredData,
        recapData: action.recapData,
        greetingLoading: action.greetingLoading,
      };
    case "set_greeting": {
      // Merge greeting data into existing recapData (firstName/lastSaved may already be set)
      const merged = action.recapData
        ? {
            firstName: action.recapData.firstName ?? state.recapData?.firstName ?? null,
            lastSaved: action.recapData.lastSaved ?? state.recapData?.lastSaved ?? null,
            summary: action.recapData.summary,
          }
        : state.recapData;
      return {
        ...state,
        recapData: merged,
        greetingText: action.greeting,
        greetingChips: action.chips,
        greetingLoading: action.loading,
      };
    }
    case "prepend_history":
      return {
        ...state,
        messages: [...action.messages, ...state.messages],
        hasMoreHistory: action.hasMore,
        oldestCursor: action.oldestCursor,
        historySummary: action.summary,
        loadingMore: false,
      };
    case "history_page_state":
      return {
        ...state,
        loadingMore: action.loadingMore ?? state.loadingMore,
        loadingHistory: action.loadingHistory ?? state.loadingHistory,
        hasMoreHistory: action.hasMoreHistory ?? state.hasMoreHistory,
        oldestCursor: action.oldestCursor === undefined ? state.oldestCursor : action.oldestCursor,
        historySummary:
          action.historySummary === undefined ? state.historySummary : action.historySummary,
        historyExpanded: action.historyExpanded ?? state.historyExpanded,
      };
    case "set_active_form":
      return { ...state, hasActiveForm: action.active, formFieldContext: action.fieldContext };
    case "set_stored_data":
      return { ...state, hasStoredData: action.hasStoredData, recapData: action.recapData };
    default:
      return state;
  }
}

const initialState: ChatState = {
  messages: [],
  status: "ready",
  error: null,
  historyLoaded: false,
  hasMoreHistory: false,
  oldestCursor: null,
  loadingMore: false,
  historySummary: null,
  greetingLoading: false,
  greetingText: null,
  greetingChips: [],
  hasStoredData: false,
  recapData: null,
  historyExpanded: false,
  loadingHistory: false,
  hasActiveForm: false,
  formFieldContext: null,
};

export function ChatPanel({
  renderDebugPanel,
  showRecap,
  initialSiteId,
  initialStartingCategory,
  surfaceIntent,
  onToolCall,
  onMessagesChange,
}: ChatPanelProps) {
  const client = useChatClient();
  const sessionId = useRef("default-session");
  const autoSentForToolCalls = useRef(new Set<string>());
  const inputRef = useRef<HTMLInputElement>(null);
  const activeFormTextSubmitRef = useRef<((text: string) => void) | null>(null);
  const thinkingStartedAtRef = useRef<number | null>(null);
  const cacheWarmedRef = useRef(false);
  const greetingPromiseRef = useRef<Promise<unknown> | null>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [debugOpen, setDebugOpen] = useState(false);
  const [siteId, setSiteId] = useState<Brand>(initialSiteId ?? "hers");
  const [startingCategory, setStartingCategory] = useState(
    initialStartingCategory ?? "weight-loss",
  );
  const [sessionReady, setSessionReady] = useState(false);
  const [experienceReady, setExperienceReady] = useState(false);
  /** Wave 0.3 — populated from client.getExperience() so the analytics
   *  context can feed variantId into the TTFC emission site. */
  const [resolvedVariantId, setResolvedVariantId] = useState<string | undefined>(undefined);
  const [showThinkingIndicator, setShowThinkingIndicator] = useState(false);
  const [thinkingCopyIndex, setThinkingCopyIndex] = useState(0);
  const [isDelayed, setIsDelayed] = useState(false);
  const [starterChips, setStarterChips] = useState<string[]>([]);
  const [starterWelcomeText, setStarterWelcomeText] = useState<string | null>(null);
  const [starterChipsNonce, setStarterChipsNonce] = useState(0);
  const setVariantOverrides = useStylesConfig((s) => s.setVariantOverrides);
  const traceMode = useStylesConfigValue("traceMode");

  // Notify parent of message changes (used by JourneyProvider)
  useEffect(() => {
    onMessagesChange?.(state.messages);
  }, [state.messages, onMessagesChange]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const existingSessionId = localStorage.getItem("chatAnonymousId") || crypto.randomUUID();
    localStorage.setItem("chatAnonymousId", existingSessionId);
    sessionId.current = existingSessionId;

    if (!initialSiteId) {
      const storedSite = (localStorage.getItem("chatSiteId") ?? "hers") as Brand;
      setSiteId(storedSite);
    }

    const params = new URLSearchParams(window.location.search);
    const urlDebug = params.get("debug") === "1";
    setDebugOpen(urlDebug);

    const legacyCategory = params.get("cap");
    if (!initialStartingCategory && legacyCategory) {
      setStartingCategory(legacyCategory);
    }

    syncToUrl({
      debug: urlDebug ? "1" : null,
      cap: legacyCategory || null,
    });

    setSessionReady(true);
  }, []);

  const visibleContentFingerprint = useMemo(
    () => getVisibleMessageFingerprint(state.messages.findLast((m) => m.role === "assistant")),
    [state.messages],
  );

  useEffect(() => {
    if (!visibleContentFingerprint) return;
    setIsDelayed(false);
    const timeoutRef = setTimeout(() => setIsDelayed(true), 1000);
    return () => clearTimeout(timeoutRef);
  }, [visibleContentFingerprint]);

  useEffect(() => {
    if (!sessionReady || !sessionId.current) return;
    if (!state.historyLoaded) return;
    persistCachedTranscript(sessionId.current, state.messages);
  }, [sessionReady, state.historyLoaded, state.messages]);

  // Eval mode: expose messages for browser eval harness to extract tool calls
  useEffect(() => {
    if ((globalThis as Record<string, unknown>).__EVAL_MODE__ === true) {
      (globalThis as Record<string, unknown>).__EVAL_MESSAGES__ = state.messages;
    }
  }, [state.messages]);

  const defaultHero = useMemo(() => getHeroText(startingCategory), [startingCategory]);
  const hero = useMemo(() => {
    if (state.recapData) {
      const name = state.recapData.firstName;
      return {
        heading: `Welcome back${name ? `, ${name}` : ""}`,
        subheading: "Let's pick up where we left off.",
      };
    }
    return defaultHero;
  }, [defaultHero, state.recapData]);
  const fallbackUrl = useMemo(
    () => getFallbackUrl(startingCategory, siteId),
    [siteId, startingCategory],
  );

  const buildRequestBase = useCallback(
    () => ({
      sessionId: sessionId.current,
      variant: readExperienceVariantOverride(),
      siteId,
      startingCategory,
      surfaceIntent,
      // Session-scoped trace-mode preference. Read from the resolved styles
      // config (variant default → debug-panel override). Server treats the
      // first-turn value as canonical; mid-session flips do not
      // retroactively change active sessions.
      traceMode: useStylesConfig.getState().getResolved().traceMode,
    }),
    [siteId, startingCategory, surfaceIntent],
  );

  // Rotate warm copy text while ThinkingIndicator is active
  useEffect(() => {
    if (!showThinkingIndicator) return;
    const interval = setInterval(() => {
      setThinkingCopyIndex((i) => (i + 1) % THINKING_COPY_POOL.length);
    }, THINKING_COPY_ROTATE_MS);
    return () => clearInterval(interval);
  }, [showThinkingIndicator]);

  const startThinkingIndicator = useCallback(() => {
    thinkingStartedAtRef.current = Date.now();
    setThinkingCopyIndex(Math.floor(Math.random() * THINKING_COPY_POOL.length));
    setShowThinkingIndicator(true);
  }, []);

  const stopThinkingIndicator = useCallback(() => {
    const MIN_THINKING_DURATION_MS = 300; // Minimum time to show thinking indicator
    const startedAt = thinkingStartedAtRef.current;
    const elapsed = startedAt ? Date.now() - startedAt : MIN_THINKING_DURATION_MS;
    const remaining = Math.max(0, MIN_THINKING_DURATION_MS - elapsed);
    window.setTimeout(() => {
      setShowThinkingIndicator(false);
    }, remaining);
  }, []);

  const streamAssistantTurn = useCallback(
    async (request: SendTurnRequest | SubmitWidgetRequest, mode: "chat" | "widget") => {
      // Wave 0.3 — TTFC t0. Fires for BOTH chat sends and widget auto-continue
      // (chatless-v1's dominant path after the first turn). See plan
      // §In-scope "Unified outgoing-turn seam".
      markTurnStart("user_send");
      try {
        const iterator =
          mode === "chat"
            ? client.sendTurn(request as SendTurnRequest)
            : client.submitWidget(request as SubmitWidgetRequest);

        // Preload each widget's chunk the first time we see its tool part in
        // the stream. The AI SDK emits a `tool-input-start` event early in
        // the turn (before the widget's input payload is finished
        // streaming), so this kicks off the JS fetch while the payload is
        // still arriving — by the time the widget mounts, the chunk is in
        // the browser module cache and Suspense does not flash.
        const preloadedToolCallIds = new Set<string>();
        let firstMessageSeen = false;
        for await (const message of iterator) {
          if (!firstMessageSeen) {
            firstMessageSeen = true;
            stopThinkingIndicator();
          }
          // Server-authoritative turnIndex flows in via UIMessage.metadata
          // (set by `messageMetadata` callback in lib/server/http/chat.ts).
          // Stamps the pending turn-timing entry so client-side beacons emit
          // in the same `(sessionId, turnIndex)` namespace as server metrics.
          // See docs/plans/done/2026-04-25-unified-turn-index-source-of-truth.md.
          const meta = (message as { metadata?: { turnIndex?: unknown } }).metadata;
          if (meta && typeof meta === "object" && typeof meta.turnIndex === "number") {
            setLatestServerTurnIndex(meta.turnIndex);
          }
          dispatch({ type: "stream_update", message });
          if (message.role === "assistant" && message.parts) {
            for (const part of message.parts) {
              if (part.type.startsWith("tool-")) {
                const toolCallId = (part as { toolCallId?: string }).toolCallId;
                if (toolCallId && !preloadedToolCallIds.has(toolCallId)) {
                  preloadedToolCallIds.add(toolCallId);
                  const toolName = part.type.replace(/^tool-/, "");
                  preloadWidgetChunk(toolName);
                }
              }
              // Fire onToolCall for each tool call in the message
              if (onToolCall && part.type === "tool-invocation") {
                const toolName = getToolNameFromPart(part);
                if (toolName) {
                  onToolCall({ toolName, args: (part as any).toolInvocation?.args ?? {} });
                }
              }
            }
          }
        }

        stopThinkingIndicator();
        dispatch({ type: "stream_finish" });
      } catch (error) {
        setShowThinkingIndicator(false);
        const message = error instanceof Error ? error.message : "request failed";
        dispatch({ type: "stream_error", error: message });
      }
    },
    [client, onToolCall, stopThinkingIndicator],
  );

  const resetSession = useCallback((category: string) => {
    const newId = crypto.randomUUID();
    clearCachedTranscript(sessionId.current);
    sessionStorage.removeItem(`cache_warm_${sessionId.current}`);
    sessionId.current = newId;
    localStorage.setItem("chatAnonymousId", newId);
    autoSentForToolCalls.current = new Set();
    cacheWarmedRef.current = false;
    greetingPromiseRef.current = null;
    setStarterChips([]);
    setStarterWelcomeText(null);
    setStarterChipsNonce((prev) => prev + 1);
    // Reset turn-timer registry + server-turnIndex tracker so the new session
    // starts clean. Without this, the previous session's tail turnIndex would
    // attribute the new session's cold-start beacon to the wrong turn (the
    // turn-timer module + tracker live at module scope, outliving sessions).
    resetTurnTimingRegistry();
    resetLatestServerTurnIndex();
    dispatch({ type: "reset", messages: [createWelcomeMessage(category)] });
  }, []);

  const handleSiteIdChange = useCallback(
    (id: string) => {
      const nextSiteId = id as Brand;
      setSiteId(nextSiteId);
      localStorage.setItem("chatSiteId", nextSiteId);
      resetSession(startingCategory);
    },
    [resetSession, startingCategory],
  );

  const handleStartingCategoryChange = useCallback(
    (id: string) => {
      setStartingCategory(id);
      resetSession(id);
    },
    [resetSession],
  );

  const clearConversation = useCallback(() => {
    autoSentForToolCalls.current = new Set();
    cacheWarmedRef.current = false;
    greetingPromiseRef.current = null;
    sessionStorage.removeItem(`cache_warm_${sessionId.current}`);
    clearCachedTranscript(sessionId.current);
    setStarterChips([]);
    setStarterWelcomeText(null);
    setStarterChipsNonce((prev) => prev + 1);
    // Wave 0.3 — clear conversation also resets turn-timer; same rationale
    // as resetSession (see there).
    resetTurnTimingRegistry();
    dispatch({ type: "reset", messages: [createWelcomeMessage(startingCategory)] });
  }, [startingCategory]);

  const handleToolOutput = useCallback(
    async (options: { tool: string; toolCallId: string; output: unknown }) => {
      const nextMessages = updateToolOutput(state.messages, {
        toolCallId: options.toolCallId,
        output: options.output,
      });
      dispatch({ type: "set_messages", messages: nextMessages });

      const lastAssistant = getLastAssistantMessage(nextMessages);
      if (!allWidgetOutputsAvailable(lastAssistant)) {
        return;
      }

      const toolKey = getWidgetToolKey(lastAssistant);
      if (!toolKey || autoSentForToolCalls.current.has(toolKey)) {
        return;
      }

      autoSentForToolCalls.current.add(toolKey);
      startThinkingIndicator();
      dispatch({ type: "stream_start", messages: nextMessages });
      await streamAssistantTurn(
        {
          ...buildRequestBase(),
          messages: nextMessages,
          widgetResponse: {
            toolName: options.tool,
            toolCallId: options.toolCallId,
            output: options.output,
          },
        },
        "widget",
      );
    },
    [buildRequestBase, startThinkingIndicator, state.messages, streamAssistantTurn],
  );

  const handleSend = useCallback(
    async (input: string) => {
      const text = input.trim();
      if (!text || state.status === "submitted" || state.status === "streaming") {
        return;
      }

      let nextMessages = state.messages;
      const lastAssistant = getLastAssistantMessage(nextMessages);
      if (lastAssistant) {
        const pendingUiTools = lastAssistant.parts.filter(
          (part) =>
            isWidgetToolPart(part) && (part as { state?: string }).state === "input-available",
        );
        if (pendingUiTools.length > 0) {
          const key = getWidgetToolKey(lastAssistant);
          if (key) {
            autoSentForToolCalls.current.add(key);
          }
          for (const part of pendingUiTools) {
            const toolName = getToolNameFromPart(part);
            if (!toolName) continue;
            nextMessages = updateToolOutput(nextMessages, {
              toolCallId: (part as { toolCallId: string }).toolCallId,
              output: {
                skipped: true,
                reason:
                  "User ignored this widget and typed a text message instead. Do NOT retry or re-render this widget. Respond to the user's text message.",
              },
            });
          }
        }
      }

      const userMessage: UIMessage = {
        id: crypto.randomUUID(),
        role: "user",
        parts: [{ type: "text", text }],
      };
      nextMessages = [...nextMessages, userMessage];

      startThinkingIndicator();
      dispatch({ type: "stream_start", messages: nextMessages });
      await streamAssistantTurn(
        {
          ...buildRequestBase(),
          messages: nextMessages,
        },
        "chat",
      );
    },
    [buildRequestBase, startThinkingIndicator, state.messages, state.status, streamAssistantTurn],
  );

  /**
   * Eval mode: expose handleSend so browser eval can inject patient messages in chatless
   * variants where the chat input (`input[name="chat-user-input"]`) doesn't exist in DOM.
   * The browser eval driver (`eval/browser/browser-driver.ts:sendMessage`) falls back to
   * this global when the input element is absent. Only active when __EVAL_MODE__ is true.
   */
  useEffect(() => {
    if ((globalThis as Record<string, unknown>).__EVAL_MODE__ === true) {
      (globalThis as Record<string, unknown>).__EVAL_SEND_MESSAGE__ = handleSend;
    }
  }, [handleSend]);

  useEffect(() => {
    if (!sessionReady || !sessionId.current || state.historyLoaded) return;

    let cancelled = false;

    const loadHistoryAndGreeting = async () => {
      if (!showRecap) {
        dispatch({
          type: "history_loaded",
          messages: [createWelcomeMessage(startingCategory)],
          hasStoredData: false,
          recapData: null,
          greetingLoading: false,
        });
        return;
      }

      const cachedTranscript = readCachedTranscript(sessionId.current);

      // Speculatively fire greeting in parallel with history — greeting is
      // cheap (returns null-response immediately) when totalTurns === 0, and
      // saves a full round-trip when totalTurns > 0. We discard the greeting
      // result below if history reports a new session.
      const historyRequest = client.getHistory({
        sessionId: sessionId.current,
        recapOnly: true,
      });
      const greetingRequest = client.getGreeting(buildRequestBase());
      // F4-B: expose greeting promise so cache-warm can sequence behind it.
      greetingPromiseRef.current = greetingRequest;

      const [historyResult, greetingResult] = await Promise.allSettled([
        historyRequest,
        greetingRequest,
      ]);
      if (cancelled) return;

      if (historyResult.status === "rejected") {
        const fallbackMessages =
          cachedTranscript.length > 0 ? cachedTranscript : [createWelcomeMessage(startingCategory)];
        dispatch({
          type: "history_loaded",
          messages: fallbackMessages,
          hasStoredData: false,
          recapData: null,
          greetingLoading: false,
        });
        return;
      }

      const history = historyResult.value;

      if (history.profile && history.profile.totalTurns > 0) {
        dispatch({
          type: "history_loaded",
          messages: [],
          hasStoredData: true,
          recapData: {
            firstName: history.profile.firstName ?? null,
            lastSaved: history.profile.lastVisit,
            summary: null,
          },
          greetingLoading: true,
        });

        // Apply the speculative greeting result now that we know the session
        // qualifies for a greeting.
        if (greetingResult.status === "fulfilled") {
          const greeting = greetingResult.value;
          dispatch({
            type: "set_greeting",
            recapData: greeting.summary
              ? {
                  summary: greeting.summary,
                  firstName: greeting.firstName,
                  lastSaved: greeting.lastSaved,
                }
              : null,
            greeting: greeting.greeting,
            chips: greeting.chips ?? [],
            loading: false,
          });
          if (greeting.chips?.length && !inputRef.current?.value) {
            // Remove focus from input to draw attention to chips
            inputRef.current?.blur();
          }
        } else {
          dispatch({
            type: "set_greeting",
            recapData: null,
            greeting: null,
            chips: [],
            loading: false,
          });
        }
        return;
      }

      // totalTurns === 0 — discard the speculative greeting (server returned
      // a null-response; nothing to render).
      if (cachedTranscript.length > 0) {
        dispatch({
          type: "history_loaded",
          messages: cachedTranscript,
          hasStoredData: false,
          recapData: null,
          greetingLoading: false,
        });
        return;
      }

      dispatch({
        type: "history_loaded",
        messages: [createWelcomeMessage(startingCategory)],
        hasStoredData: false,
        recapData: null,
        greetingLoading: false,
      });
    };

    loadHistoryAndGreeting();
    return () => {
      cancelled = true;
    };
  }, [buildRequestBase, client, sessionReady, showRecap, startingCategory, state.historyLoaded]);

  // Opportunistic cache warm — fire once per session before the user's first message.
  // If user types before warm-up completes, turn 1 falls back to today's behavior.
  // Stores warm expiry in sessionStorage so page reloads skip the request entirely.
  //
  // F4-B (2026-05-07) — Sequence cache-warm AFTER greeting on the React host
  // to avoid singleton-Bedrock-client TLS-handshake contention on dev cold-start
  // (see docs/plans/2026-05-05-flash-proposal-greeting-latency_flash.md).
  //
  // SDK boundary note: this fix lives in the React renderer tier. Non-React hosts
  // consuming @health/sdk-client directly (none today; mobile/partner-embed
  // planned) will NOT inherit this sequencing. If/when a second host lands, lift
  // the sequencing primitive into @health/sdk-client as an opt-in helper rather
  // than re-implementing per-renderer.
  useEffect(() => {
    if (cacheWarmedRef.current) return;
    if (!state.historyLoaded) return;
    // Returning users: defer until greeting completes so session is fully hydrated
    if (state.hasStoredData && state.greetingLoading) return;
    cacheWarmedRef.current = true;

    // Skip if already warmed (survives page reloads within the cache TTL)
    const storageKey = `cache_warm_${sessionId.current}`;
    const cached = sessionStorage.getItem(storageKey);
    if (cached) {
      const expiresAt = Number(cached);
      if (Date.now() < expiresAt) return;
    }

    // Sequence cache-warm strictly after greeting so the two Bedrock calls don't
    // contend for the singleton client's cold TLS handshake. greetingPromiseRef
    // is null when greeting wasn't fired (e.g. !showRecap path) — fire immediately.
    const greetingPromise = greetingPromiseRef.current ?? Promise.resolve();
    greetingPromise
      .catch(() => {})
      .then(() =>
        client.warmCache(buildRequestBase()).then((res) => {
          if (res.warmed && res.ttlMs) {
            sessionStorage.setItem(storageKey, String(Date.now() + res.ttlMs));
          }
        }),
      )
      .catch(() => {});
  }, [buildRequestBase, client, state.greetingLoading, state.hasStoredData, state.historyLoaded]);

  // Sync variant UI settings into styles config as variant overrides.
  // Gates the main render so the chat layout doesn't flicker (e.g. input
  // bar shown then hidden) before the experience response arrives.
  useEffect(() => {
    if (!sessionReady) return;

    let cancelled = false;
    const syncVariantStyles = async () => {
      try {
        const experience = await client.getExperience(buildRequestBase());
        if (cancelled) return;
        const { chat_input_mode, scroll_snap, trace_mode, auth_mode } = experience.variant
          .settings as {
          chat_input_mode?: "persistent" | "inline";
          scroll_snap?: boolean;
          trace_mode?: "deliberate" | "plan" | "disabled";
          auth_mode?: "blocking" | "dismissible";
        };
        setVariantOverrides({
          overrides: {
            showChatInput: (chat_input_mode ?? "persistent") === "persistent",
            ...(scroll_snap != null && { scrollSnap: scroll_snap as boolean }),
            ...(trace_mode != null && { traceMode: trace_mode }),
            ...(auth_mode != null && { authMode: auth_mode }),
          },
        });
        // Wave 0.3 — surface variantId into analytics context.
        const variantId =
          typeof experience.variant.variant?.id === "string"
            ? experience.variant.variant.id
            : undefined;
        if (variantId) setResolvedVariantId(variantId);
      } catch {
        // Keep defaults on error
      } finally {
        if (!cancelled) setExperienceReady(true);
      }
    };

    syncVariantStyles();
    return () => {
      cancelled = true;
    };
  }, [buildRequestBase, client, sessionReady, setVariantOverrides]);

  useEffect(() => {
    const hasUserMessages = state.messages.some((message) => message.role === "user");
    if (
      !sessionReady ||
      !state.historyLoaded ||
      state.hasStoredData ||
      state.recapData ||
      state.historyExpanded ||
      hasUserMessages
    ) {
      setStarterChips([]);
      setStarterWelcomeText(null);
      return;
    }

    let cancelled = false;

    const loadStarterChips = async () => {
      try {
        const experience = await client.getExperience(buildRequestBase());
        if (cancelled) return;
        const chips = experience.variant.settings.response_chips
          ? (experience.variant.conversation_start?.start_questions ?? [])
          : [];
        const welcomeText = experience.variant.conversation_start?.welcome_message ?? null;
        setStarterChips(chips);
        setStarterWelcomeText(welcomeText);

        // Remove focus from input to draw attention to chips. (if not already started typing)
        if (chips.length && !inputRef.current?.value) {
          inputRef.current?.blur();
        }
      } catch {
        if (!cancelled) {
          setStarterChips([]);
          setStarterWelcomeText(null);
        }
      }
    };

    loadStarterChips();
    return () => {
      cancelled = true;
    };
  }, [
    buildRequestBase,
    client,
    sessionReady,
    starterChipsNonce,
    state.hasStoredData,
    state.historyExpanded,
    state.historyLoaded,
    state.messages,
    state.recapData,
  ]);

  useEffect(() => {
    const hasUserMessages = state.messages.some((message) => message.role === "user");
    if (
      !sessionReady ||
      !state.historyLoaded ||
      state.hasStoredData ||
      state.recapData ||
      state.historyExpanded ||
      hasUserMessages
    ) {
      return;
    }

    const expectedMessage = createWelcomeMessage(startingCategory, starterWelcomeText ?? undefined);

    if (state.messages.length === 0) {
      dispatch({ type: "set_messages", messages: [expectedMessage] });
      return;
    }

    if (
      state.messages.length !== 1 ||
      state.messages[0]?.id !== "welcome" ||
      state.messages[0]?.role !== "assistant"
    ) {
      return;
    }

    const firstPart = state.messages[0].parts[0];
    const currentText = firstPart?.type === "text" ? firstPart.text : null;
    const nextText =
      expectedMessage.parts[0]?.type === "text" ? expectedMessage.parts[0].text : null;
    if (currentText !== nextText) {
      dispatch({ type: "set_messages", messages: [expectedMessage] });
    }
  }, [
    sessionReady,
    starterWelcomeText,
    startingCategory,
    state.hasStoredData,
    state.historyExpanded,
    state.historyLoaded,
    state.messages,
    state.recapData,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (state.loadingMore || !state.hasMoreHistory || !state.oldestCursor) return;
    dispatch({ type: "history_page_state", loadingMore: true });
    try {
      const history = await client.getHistory({
        sessionId: sessionId.current,
        before: state.oldestCursor,
        limit: SESSION_HISTORY_PAGE_SIZE,
      });
      dispatch({
        type: "prepend_history",
        messages: history.messages,
        hasMore: history.hasMore,
        oldestCursor: history.oldestTimestamp,
        summary: history.summary,
      });
    } catch {
      dispatch({ type: "history_page_state", loadingMore: false });
    }
  }, [client, state.hasMoreHistory, state.loadingMore, state.oldestCursor]);

  const handleActiveFormChange = useCallback(
    (
      active: boolean,
      onTextSubmit?: (text: string) => void,
      fieldContext?: { placeholder?: string; fieldType?: string },
    ) => {
      activeFormTextSubmitRef.current = onTextSubmit ?? null;
      dispatch({ type: "set_active_form", active, fieldContext: fieldContext ?? null });
    },
    [],
  );

  const handleFormTextSubmit = useCallback((text: string) => {
    activeFormTextSubmitRef.current?.(text);
  }, []);

  const isLoading = state.status === "submitted" || state.status === "streaming";
  const hasUserMessages = state.messages.some((message) => message.role === "user");

  // Hide the bottom ThinkingIndicator when the streaming assistant message
  // already has visible inline content (server tool activity status or text).
  // This prevents both the bottom indicator and inline status showing simultaneously.
  const hasInlineActivityStatus = useMemo(() => {
    if (traceMode === "disabled") return false;
    if (state.status !== "streaming") return false;
    const lastMsg = state.messages[state.messages.length - 1];
    if (!lastMsg || lastMsg.role !== "assistant") return false;
    const part = lastMsg.parts.at(-1);
    if (part?.type === "text") return true;
    if (!part?.type.startsWith("tool-")) return false;
    const toolPart = part as { type: string; state?: string };
    // Resolve aliases so legacy tool names (e.g. `signal_status`) are
    // recognized — matches `normalize.ts` lookup behavior.
    const toolName = String(resolveToolName(toolPart.type.replace("tool-", "")));
    return hasVisibleStatus(toolName) && toolPart.state !== "output-available";
  }, [state.status, state.messages]);

  const [isInlineActivityStatusShowing, setIsInlineActivityStatusShowing] = useState(false);
  useEffect(() => {
    if (hasInlineActivityStatus) {
      setIsInlineActivityStatusShowing(true);
      return;
    }

    const timeoutRef = window.setTimeout(() => {
      setIsInlineActivityStatusShowing(false);
    }, ACTIVITY_STATUS_AUTO_FADE_MS);

    return () => {
      if (timeoutRef) {
        window.clearTimeout(timeoutRef);
      }
    };
  }, [hasInlineActivityStatus]);

  const showStarterChips =
    starterChips.length > 0 &&
    !state.recapData &&
    !state.historyExpanded &&
    !state.hasActiveForm &&
    !state.error &&
    !isLoading &&
    !hasUserMessages;

  const showGreetingChips = state.greetingChips.length > 0 && !hasUserMessages;

  // Autofocus chat input when status becomes ready and no widget is awaiting input
  const hasPendingWidgetInput = useMemo(() => {
    if (showGreetingChips || showStarterChips) return false;
    const lastAssistant = getLastAssistantMessage(state.messages);
    if (!lastAssistant) return false;
    return lastAssistant.parts.some(
      (part) => isWidgetToolPart(part) && (part as { state?: string }).state === "input-available",
    );
  }, [state.messages, showGreetingChips, showStarterChips]);

  useEffect(() => {
    if (state.status === "ready" && !state.hasActiveForm && !hasPendingWidgetInput) {
      inputRef.current?.focus();
    }
  }, [state.status, state.hasActiveForm, hasPendingWidgetInput]);

  const hasUserMessageParts = useMemo(
    () =>
      state.messages.some((m) => m.role === "user" || m.parts.some((p) => isUserMessagePart(m, p))),
    [state.messages],
  );

  const thinkingActive =
    (showThinkingIndicator || isLoading) && !isInlineActivityStatusShowing && isDelayed;
  const thinkingText = THINKING_COPY_POOL[thinkingCopyIndex];

  const preambleItems = useMemo(() => {
    const items: ChatItem[] = [
      { kind: "hero", id: "hero", heading: hero.heading, subheading: hero.subheading },
    ];
    if (state.recapData) {
      items.push({
        kind: "recap",
        id: "recap",
        summary: state.recapData.summary,
        firstName: state.recapData.firstName,
        lastSaved: state.recapData.lastSaved,
      });
    }
    if (state.greetingText) {
      items.push({ kind: "text", id: "greeting", role: "assistant", text: state.greetingText });
    }
    if (showGreetingChips) {
      items.push({
        kind: "widget",
        id: "greeting-chips",
        toolName: "show_chips",
        toolCallId: "greeting-chips",
        state: "active",
        input: {
          options: state.greetingChips.map((c) => ({ id: c, label: c })),
          multi_select: false,
          include_fallback: false,
          show_instruction: true,
        },
      });
      items.push({ kind: "privacy-note", id: "greeting-privacy" });
    }
    return items;
  }, [hero, state.recapData, state.greetingText, showGreetingChips, state.greetingChips]);

  const chatGroups = useMemo(() => {
    const groups = normalizeChatItems(state.messages, {
      isStreaming: state.status === "streaming",
      preambleItems,
    });
    if (showStarterChips) {
      const last = groups[groups.length - 1];
      last.items.push({
        kind: "widget",
        id: "starter-chips",
        toolName: "show_chips",
        toolCallId: "starter-chips",
        state: "active",
        input: {
          options: starterChips.map((c) => ({ id: c, label: c })),
          multi_select: false,
          include_fallback: false,
          show_instruction: true,
        },
      });
      last.items.push({ kind: "privacy-note", id: "starter-privacy" });
    }
    // Thinking goes at the end of the last group — unless that group already
    // contains a transition card. The card itself signals that the next step
    // is loading (and shows its own spinner if the user clicks early), so an
    // additional dots loader would be redundant noise.
    const lastGroup = groups[groups.length - 1];
    const hasTransition = lastGroup.items.some((i) => i.kind === "transition");
    if (!hasTransition) {
      const thinkingItem: ChatItem = {
        kind: "thinking",
        id: "thinking",
        active: thinkingActive,
        text: thinkingText,
      };
      lastGroup.items.push(thinkingItem);
    }
    return groups;
  }, [
    preambleItems,
    state.messages,
    state.status,
    thinkingActive,
    thinkingText,
    showStarterChips,
    starterChips,
  ]);

  const handleSyntheticChipOutput = useCallback(
    async (options: { tool: string; toolCallId: string; output: unknown }) => {
      const output = options.output as { selected?: string[] };
      const selected = output?.selected?.[0];
      if (selected) await handleSend(selected);
    },
    [handleSend],
  );

  const renderWidget = useCallback(
    (item: ChatWidgetItem) => {
      const isSynthetic = SYNTHETIC_CHIP_IDS.has(item.toolCallId);
      const outputHandler = isSynthetic ? handleSyntheticChipOutput : handleToolOutput;

      // Widget-specific suppression checks — return null so renderItem in
      // ChatFeed skips the animate-fade-in wrapper entirely (no gap ghosts).
      if (
        resolveToolName(item.toolName) === "show_meducation" &&
        isMeducationSuppressed(item.output)
      ) {
        return null;
      }

      if (item.state === "active") {
        return (
          <div style={{ viewTransitionName: "widget-active" }}>
            <WidgetRegistry
              toolName={item.toolName}
              toolCallId={item.toolCallId}
              input={item.input}
              output={item.output}
              addToolOutput={outputHandler}
              streaming={item.streaming}
              historic={item.historic}
            />
          </div>
        );
      }

      // Skipped without output = historical widget user never interacted with
      const wasSubmitted = !(item.state === "skipped" && !item.output);
      return (
        <WidgetRegistry
          toolName={item.toolName}
          toolCallId={item.toolCallId}
          input={item.input}
          output={item.output ?? undefined}
          addToolOutput={outputHandler}
          submitted={wasSubmitted}
          historic={item.historic}
        />
      );
    },
    [handleToolOutput, handleSyntheticChipOutput],
  );

  const renderStatus = useCallback((item: ChatStatusItem) => {
    if (item.variant === "signal") {
      return <StatusIndicator status={item.status} context={item.context} />;
    }
    return (
      <ToolActivityStatus
        toolName={item.toolName}
        toolCallId={item.toolCallId!}
        input={item.input!}
        state={item.toolState as "input-available" | "output-available"}
        isLive={item.isLive ?? false}
        siblingToolNames={item.siblingToolNames}
      />
    );
  }, []);

  if (!experienceReady) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center w-full">
        <Spinner $size="lg" />
      </div>
    );
  }

  return (
    <AnalyticsProvider
      value={{
        sessionId: sessionId.current,
        site: siteId,
        visit_type: startingCategory,
        variantId: resolvedVariantId,
      }}
    >
      <TurnTimingProvider>
        <ChatLayout
          headerSlot={renderDebugPanel?.({
            sessionId: sessionId.current,
            chatStatus: state.status,
            siteId,
            onSiteIdChange: handleSiteIdChange,
            startingCategory,
            onStartingCategoryChange: handleStartingCategoryChange,
            onClearHistory: () => resetSession(startingCategory),
            onClearConversation: clearConversation,
            isOpen: debugOpen,
            onToggle: () =>
              setDebugOpen((open) => {
                const next = !open;
                queueMicrotask(() => syncToUrl({ debug: next ? "1" : null }));
                return next;
              }),
          })}
          errorSlot={
            state.error ? (
              <ChatErrorCard fallbackUrl={fallbackUrl} onRetry={() => window.location.reload()} />
            ) : undefined
          }
          inputSlot={
            <div className="flex justify-center">
              <div className="max-w-2xl w-full px-6 pt-4">
                <div className="rounded-t-4xl bg-white shadow-lg">
                  <ActiveFormLayer
                    messages={state.messages}
                    addToolOutput={handleToolOutput}
                    onActiveFormChange={handleActiveFormChange}
                  />
                  <ChatInput
                    onMessageSubmit={state.hasActiveForm ? handleFormTextSubmit : handleSend}
                    disabled={isLoading}
                    placeholder={
                      state.hasActiveForm && state.formFieldContext
                        ? state.formFieldContext.placeholder || "Type your answer..."
                        : state.hasActiveForm
                          ? "Type your answer or select above..."
                          : `Ask ${siteId}...`
                    }
                    activeFormContext={state.hasActiveForm}
                    className={cn(state.status === "submitted" && "opacity-80")}
                    ref={inputRef}
                  />
                </div>
              </div>
            </div>
          }
        >
          <ChatFeed
            groups={chatGroups}
            renderWidget={renderWidget}
            renderText={defaultRenderText}
            renderStatus={renderStatus}
            renderLoading={(item) => <WidgetLoadingRegistry item={item} />}
            onLoadMore={handleLoadMore}
            hasMoreHistory={state.hasMoreHistory}
            isLoadingMore={state.loadingMore}
            historySummary={state.historySummary}
            showFlexSpacer={!state.recapData}
            hasContent={hasUserMessageParts}
            status={state.status}
          />
        </ChatLayout>
      </TurnTimingProvider>
    </AnalyticsProvider>
  );
}
